import { FormEvent, useState } from 'react';
import { Globe2 } from 'lucide-react';
import PageHero from '../../components/PageHero';
import { FieldLabel, TextInput, TextTextarea, FormButton, HoneypotInput } from '../../components/forms/FormFields';
import RecaptchaField from '../../components/RecaptchaField';
import { notify } from '../../components/notify/notify';
import { useSilentGuard } from '../../hooks/useSilentGuard';
import { useCaptcha } from '../../hooks/useCaptcha';
import { submitCountryRequest } from '../../services/leads';
import { clientError, ServerErrorCode, toAppError } from '../../lib/errorCodes';
import { EMAIL_RE } from '../../lib/validation';

const EMPTY_FORM = { name: '', email: '', countryNeeded: '', reason: '' };
const FALLBACK_EMAIL = 'one@lab63.org';

export default function CountryNotListedPage() {
  const { state, setHoneypot, validate, reset } = useSilentGuard();
  const captcha = useCaptcha();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const countryNeeded = form.countryNeeded.trim();
    const reason = form.reason.trim();

    if (!name || !email || !countryNeeded || !reason) {
      notify.appError(clientError('REQUIRED_FIELDS', 'Please fill in your name, email, country, and reason.'));
      return;
    }
    if (!EMAIL_RE.test(email)) {
      notify.appError(clientError('INVALID_EMAIL'));
      return;
    }
    if (!captcha.verified) {
      notify.appError(clientError('CAPTCHA_REQUIRED'));
      return;
    }

    const gate = await validate();
    if (!gate.ok) {
      notify.appError(gate.error);
      return;
    }

    setLoading(true);
    try {
      await submitCountryRequest({
        name,
        email,
        countryNeeded,
        reason,
        captchaId: captcha.verified.captchaId,
        captchaAnswer: captcha.verified.captchaAnswer,
        silent: gate.payload,
      });
      notify.success({
        title: 'Request sent',
        message: `Thank you — we’ll use your request to prioritise Micus in ${countryNeeded}.`,
      });
      setForm(EMPTY_FORM);
      reset();
      await captcha.reload();
    } catch (err) {
      const info = toAppError(err);
      const serverDown = info.code.startsWith('MIC-NET') || (info.status ?? 0) >= 500;
      notify.appError(
        info,
        serverDown
          ? {
              action: {
                label: 'Email us instead',
                onClick: () => {
                  const subject = encodeURIComponent(`Micus country request: ${countryNeeded}`);
                  const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\nCountry needed: ${countryNeeded}\n\nReason:\n${reason}`);
                  window.location.href = `mailto:${FALLBACK_EMAIL}?subject=${subject}&body=${body}`;
                },
              },
            }
          : {},
      );
      if (info.code === ServerErrorCode.CAPTCHA_FAILED) await captcha.reload();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        badge="Universal · Not listed"
        title="Need Micus"
        titleAccent="in your country?"
        subtitle="Tell us which country you need and why. We’ll use your request to prioritise expansion."
      />
      <section style={{ padding: '56px 0 72px', background: '#FFFBF8' }}>
        <div className="container-wide" style={{ maxWidth: 560 }}>
          <div style={{ background: 'white', borderRadius: 22, border: '1px solid #F0C8D8', padding: 28, boxShadow: '0 10px 40px rgba(194,24,91,0.08)' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 22 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Globe2 size={20} color="#fff" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>Country expansion request</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#6B3A52', lineHeight: 1.5 }}>
                  We currently support India and the United States. Share your market need below.
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} style={{ display: 'grid', gap: 14 }}>
              <HoneypotInput value={state.honeypot} onChange={setHoneypot} />
              <div>
                <FieldLabel htmlFor="nl-name">Your name</FieldLabel>
                <TextInput id="nl-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div>
                <FieldLabel htmlFor="nl-email">Email</FieldLabel>
                <TextInput id="nl-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <FieldLabel htmlFor="nl-country">Country you need Micus in</FieldLabel>
                <TextInput id="nl-country" value={form.countryNeeded} onChange={(e) => setForm((f) => ({ ...f, countryNeeded: e.target.value }))} placeholder="e.g. Singapore, UAE, UK…" required />
              </div>
              <div>
                <FieldLabel htmlFor="nl-reason">Why do you need Micus there?</FieldLabel>
                <TextTextarea id="nl-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Tell us about your raise, sector, or market need…" required />
              </div>
              <RecaptchaField
                challenge={captcha.challenge}
                loading={captcha.loading}
                onRefresh={() => void captcha.reload()}
                onVerified={captcha.setVerified}
              />
              <FormButton type="submit" loading={loading}>
                Send request
              </FormButton>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
